import React from "react";
import { useTranslation } from "react-i18next";
import Header from "../header/HeaderComponent";
import Footer from "../footer/FooterComponent";
import Seo from "../Seo/SeoComponent";
import { getStudyMaterialLink, studyMaterials } from "../../consts/studyMaterials";
import styles from "./studyMaterialsPage.module.scss";
import PageLanguageLink from "../PageLanguageLink/PageLanguageLink";

function BookCover({ material }: { material: (typeof studyMaterials)[number] }) {
    const { t } = useTranslation("studyMaterialsPage");
    if (material.coverImage) {
        return (
            <img
                className={styles.bookCoverImage}
                src={material.coverImage.src}
                alt={t(`materials.${material.id}.coverAlt`, { defaultValue: material.coverImage.alt })}
                loading="lazy"
            />
        );
    }

    return (
        <div className={styles.bookCover} role="img" aria-label={t("placeholderCoverAlt", { title: material.title })}>
            <span className={styles.coverTurtle} aria-hidden="true">🐢</span>
            <span className={styles.coverSeries}>GENKI I</span>
            <span className={styles.coverType}>{t(`materials.${material.id}.type`, { defaultValue: material.type })}</span>
        </div>
    );
}

function StudyMaterialsPage() {
    const { t, i18n } = useTranslation("studyMaterialsPage");
    const isPt = i18n.language === "pt";
    const switchPath = isPt ? "/study-materials" : "/pt/study-materials";
    const currentPath = isPt ? "/pt/study-materials" : "/study-materials";

    return (
        <div className={styles.page}>
            <Seo
                title={t("seoTitle")}
                description={t("seoDescription")}
                path={currentPath}
                lang={isPt ? "pt-PT" : undefined}
            />
            <Header />

            <main id="main-content" className={styles.main}>
                <section className={styles.container} aria-labelledby="study-materials-title">
                    <p className={styles.eyebrow}>{t("eyebrow")}</p>
                    <h1 id="study-materials-title" className={styles.title}>{t("title")}</h1>
                    <p className={styles.introduction}>{t("introduction")}</p>
                    <aside className={styles.teacherNote} aria-label={t("teacherNoteLabel")}>
                        <span className={styles.noteTurtle} aria-hidden="true">🐢</span>
                        <p>{t("teacherNote")}</p>
                    </aside>

                    <div className={styles.materialList}>
                        {studyMaterials.map((material) => {
                            const link = getStudyMaterialLink(material, "materialsPage");

                            return (
                                <article key={material.id} className={styles.materialCard}>
                                    <BookCover material={material} />
                                    <div className={styles.cardContent}>
                                        <div className={styles.typeRow}>
                                            <p className={styles.resourceType}>{t(`materials.${material.id}.type`, { defaultValue: material.type })}</p>
                                            {material.jlptLevel && (
                                                <span className={styles.jlptBadge}>{t("goodFor", { level: material.jlptLevel })}</span>
                                            )}
                                        </div>
                                        <h2 className={styles.materialTitle}>{material.title}</h2>
                                        <p className={styles.description}>{t(`materials.${material.id}.description`, { defaultValue: material.description })}</p>
                                        <dl className={styles.details}>
                                            <div>
                                                <dt>{t("suitableFor")}</dt>
                                                <dd>{t(`materials.${material.id}.suitableFor`, { defaultValue: material.suitableFor })}</dd>
                                            </div>
                                            <div>
                                                <dt>{t("whyRecommended")}</dt>
                                                <dd>{t(`materials.${material.id}.recommendation`, { defaultValue: material.recommendation })}</dd>
                                            </div>
                                        </dl>
                                        {material.details && (
                                            <dl className={styles.bookDetails} aria-label={t("editionDetailsLabel", { title: material.title })}>
                                                {material.details.map((detail) => (
                                                    <div key={detail.label}>
                                                        <dt>{t(`detailLabels.${detail.label}`, { defaultValue: detail.label })}</dt>
                                                        <dd>{t(`detailValues.${detail.value}`, { defaultValue: detail.value })}</dd>
                                                    </div>
                                                ))}
                                            </dl>
                                        )}
                                        {link.href ? (
                                            <a
                                                className={styles.action}
                                                href={link.href}
                                                target="_blank"
                                                rel={link.isAffiliate ? "sponsored noopener noreferrer" : "noopener noreferrer"}
                                                data-tracking-id={link.trackingId || undefined}
                                            >
                                                {t(`materials.${material.id}.ctaLabel`, { defaultValue: link.ctaLabel })}<span className={styles.actionArrow} aria-hidden="true">→</span>
                                            </a>
                                        ) : (
                                            <button type="button" className={styles.action} disabled title={t("linkComingSoonTitle")}>
                                                {t("viewBook")} <span className={styles.comingSoon}>{t("linkComingSoon")}</span>
                                            </button>
                                        )}
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                    <PageLanguageLink to={switchPath} />
                </section>
            </main>
            <Footer />
        </div>
    );
}

export default StudyMaterialsPage;
